import { A11yModule } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, CdkOverlayOrigin, type ConnectedPosition } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { DkTooltip } from '@dkds/ng-components';
import { LucideAngularModule } from 'lucide-angular';

import { ProjectsDataDto } from '../../models/dashboard.dto';
import { ProjectPanelService } from '../../services/project-panel.service';
import { MAX_PINNED_PROJECTS, ProjectSelectionService } from '../../services/project-selection.service';
import { ProjectService } from '../../services/project.service';
import { PinIconComponent } from './pin-icon.component';
import { ProjectAvatarComponent } from './project-avatar.component';

/** A search hit, split around the matched text so the template can embolden it. */
interface ProjectMatch {
  project: ProjectsDataDto;
  before: string;
  match: string;
  after: string;
}

/** Below the bar, and above it when there is no room below. */
const PANEL_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
];

/** How many selected projects the box names before it sums up the rest as "+N". */
const MAX_BOX_CHIPS = 3;

const byName = (a: ProjectsDataDto, b: ProjectsDataDto) => a.name.localeCompare(b.name);

/**
 * The page header's project filter: All Projects, the pinned projects as chips, a box naming the
 * other selected projects, and a panel to search, select and pin from.
 *
 * The selection lives in `ProjectSelectionService`, read straight from its signals, so this
 * component keeps no copy of it. An empty selection *is* All Projects — the pages that read the
 * selection already treat it that way — so there is nothing to default on load.
 */
@Component({
  selector: 'app-project-selector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    A11yModule,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    DkTooltip,
    LucideAngularModule,
    NgTemplateOutlet,
    PinIconComponent,
    ProjectAvatarComponent,
  ],
  templateUrl: './project-selector.component.html',
  styleUrl: './project-selector.component.scss',
})
export class ProjectSelectorComponent {
  private readonly projectService = inject(ProjectService);
  private readonly projectPanelService = inject(ProjectPanelService);
  private readonly selection = inject(ProjectSelectionService);

  readonly projects = input<readonly ProjectsDataDto[]>([]);

  protected readonly maxPinned = MAX_PINNED_PROJECTS;
  protected readonly panelPositions = PANEL_POSITIONS;
  protected readonly panelId = 'project-selector-panel';

  protected readonly open = signal(false);
  protected readonly query = signal('');
  /** Set when a pin is refused because the bar is full; the panel says so until a pin frees up. */
  protected readonly pinLimitHit = signal(false);

  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');

  /** Persisted ids outlive the projects they name — a deleted project, or another org's. */
  private readonly known = computed(() => new Map(this.projects().map((project) => [project.id, project])));

  protected readonly selectedIds = computed(() => {
    const known = this.known();
    return new Set([...this.selection.selectedProjectIds()].filter((id) => known.has(id)));
  });

  protected readonly isAll = computed(() => this.selectedIds().size === 0);

  protected readonly pinnedProjects = computed(() => {
    const known = this.known();
    return this.selection
      .pinnedProjectOrder()
      .map((id) => known.get(id))
      .filter((project): project is ProjectsDataDto => project !== undefined);
  });

  protected readonly pinnedIds = computed(() => new Set(this.pinnedProjects().map((project) => project.id)));

  /**
   * Selected but not pinned. A selected pinned project already shows as pressed on its own chip,
   * so the box never names it a second time.
   */
  protected readonly boxProjects = computed(() =>
    this.projects()
      .filter((project) => this.selectedIds().has(project.id) && !this.pinnedIds().has(project.id))
      .sort(byName),
  );

  protected readonly boxShown = computed(() => this.boxProjects().slice(0, MAX_BOX_CHIPS));
  protected readonly boxOverflow = computed(() => this.boxProjects().slice(MAX_BOX_CHIPS));

  /** Only pinned projects selected is still a selection, so the box asks for more rather than any. */
  protected readonly placeholder = computed(() => (this.isAll() ? 'Select projects' : 'More projects'));

  /** The box's chips are decoration; its name is what carries every selected project it holds. */
  protected readonly boxLabel = computed(() => {
    const box = this.boxProjects();
    if (box.length === 0) return 'Choose projects';
    return `Choose projects, ${box.length} selected: ${box.map((project) => project.name).join(', ')}`;
  });

  protected readonly overflowTooltip = computed(() => {
    const overflow = this.boxOverflow();
    return overflow.length > 0 ? `Also selected: ${overflow.map((project) => project.name).join(', ')}` : '';
  });

  protected readonly sortedProjects = computed(() => [...this.projects()].sort(byName));

  /** The panel's "All projects" group; the pinned ones already have a group of their own. */
  protected readonly unpinnedProjects = computed(() =>
    this.sortedProjects().filter((project) => !this.pinnedIds().has(project.id)),
  );

  protected readonly matches = computed<ProjectMatch[]>(() => {
    const query = this.query().trim().toLowerCase();
    if (!query) return [];

    return this.sortedProjects().flatMap((project) => {
      const at = project.name.toLowerCase().indexOf(query);
      if (at < 0) return [];
      return [
        {
          project,
          before: project.name.slice(0, at),
          match: project.name.slice(at, at + query.length),
          after: project.name.slice(at + query.length),
        },
      ];
    });
  });

  protected readonly searching = computed(() => this.query().trim().length > 0);

  // ── Bar ────────────────────────────────────────────────────────────────────

  protected selectAll(): void {
    this.selection.setSelectedProjectIds([]);
  }

  /** A chip narrows to that one project; pressing it again, while it is the only one, widens back out. */
  protected selectOnly(project: ProjectsDataDto): void {
    const selected = this.selectedIds();
    if (selected.size === 1 && selected.has(project.id)) {
      this.selectAll();
      return;
    }
    this.commitSelection([project.id]);
  }

  // ── Panel ──────────────────────────────────────────────────────────────────

  protected toggleOpen(): void {
    if (this.open()) {
      this.close();
    } else {
      this.query.set('');
      this.pinLimitHit.set(false);
      this.open.set(true);
    }
  }

  /** Closes the panel. Focus goes back to the control that opened it, wherever it was dismissed from. */
  protected close(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.query.set('');
    this.trigger().nativeElement.focus();
  }

  protected onPanelKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  protected onSearch(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  protected clearSearch(search: HTMLInputElement): void {
    this.query.set('');
    search.focus();
  }

  protected isSelected(project: ProjectsDataDto): boolean {
    return this.selectedIds().has(project.id);
  }

  protected isPinned(project: ProjectsDataDto): boolean {
    return this.pinnedIds().has(project.id);
  }

  protected toggleSelection(project: ProjectsDataDto): void {
    const ids = new Set(this.selectedIds());
    if (ids.has(project.id)) {
      ids.delete(project.id);
    } else {
      ids.add(project.id);
    }
    this.commitSelection([...ids]);
  }

  /**
   * The All projects row is a checkbox that can only be ticked: unticking "everything" has no
   * meaning of its own, so a press while it is already ticked leaves it ticked.
   */
  protected onAllRowChange(event: Event): void {
    (event.target as HTMLInputElement).checked = true;
    this.selectAll();
  }

  protected togglePin(project: ProjectsDataDto): void {
    const order = this.pinnedProjects().map((p) => p.id);

    if (order.includes(project.id)) {
      this.selection.setPinnedProjectIds(order.filter((id) => id !== project.id));
      this.pinLimitHit.set(false);
      return;
    }

    if (order.length < MAX_PINNED_PROJECTS) {
      this.selection.setPinnedProjectIds([...order, project.id]);
      return;
    }

    this.pinLimitHit.set(true);
  }

  protected openCreateProject(): void {
    this.close();
    this.projectPanelService.openCreatePanel();
  }

  /**
   * Writes the selection, and keeps `ProjectService`'s single active project in step whenever the
   * selection comes down to one: the single-project pages read that, not the selection.
   */
  private commitSelection(ids: readonly string[]): void {
    this.selection.setSelectedProjectIds(ids);

    if (ids.length === 1) {
      const project = this.known().get(ids[0]);
      if (project) {
        this.projectService.setProject({
          id: project.id,
          name: project.name,
          description: project.description,
          color: project.color,
          logo: project.logo,
        });
      }
    }
  }
}
